import React, { useMemo, useState } from 'react';
import { useModal } from 'react-modal-hook';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import isEqual from 'lodash/isEqual';
import { Alert, AlertContent, Empty, LayoutSidebar, ModalFactory, SectionHeader, TipCalcLaborDataSidebar } from 'components';
import { EmployeeLaborJobErrorType, IEmployeeLaborJobError, IEmployeeLaborJobState, ILaborDataPayee } from 'screens/MakePayment/Calculator/types';
import { selectCalculatorChangeLog, selectCalculatorPaymentSet, selectCalculatorPOSResults, setPOSResults } from 'store/TipCalculator';
import { ChangeLogAction, GracePeriodTimeWorked, ICalculatorRules, IDaySegmentGracePeriodPolicy, ISite, ITipCalculatorChangeLogEntry } from 'types';
import { Duration } from 'utils/duration';
import { buildLaborChangeLogEntries, buildLaborDataPayees, calculateTipCalculatorLaborDataTotals, getRuleEligibleJobTitles } from 'utils/tipCalculator';
import { MPContent } from '../../styles';
import EmployeeListItem from '../components/LaborData/EmployeeListItem/EmployeeListItem';
import { TipCalculatorProcessingModal } from '../components/modals/TipCalculatorProcessingModal';
import { TipCalculatorProcessingModalMode } from '../components/modals/TipCalculatorProcessingModal/types';

interface ILaborDataProps {
  currentCalculatorRules: ICalculatorRules;
  site: ISite;
  onBack: () => void;
  pageHeaderContent: React.ReactNode;
}

const REMOVE_EMPLOYEE_MESSAGE =
  "If you remove this employee, all their tippable hours will be deleted. Additionally, any tips they've collected will be excluded from calculations. As a result, they will neither receive nor contribute to tip distributions.";

const SPLIT_CHECK_REMOVE_MESSAGE = "We're sorry, but you cannot remove this employee as they are currently associated with a Split Check.";

const getTotalSegmentMinutes = (start: string, end: string) => {
  try {
    const segmentDurationMinutes = Duration.convertDurationStringToMins(end) - Duration.convertDurationStringToMins(start);
    if (segmentDurationMinutes < 0) {
      const hours = Math.abs(segmentDurationMinutes) / 60;
      return Math.round((24 - hours) * 60);
    }

    return segmentDurationMinutes;
  } catch {
    return -1;
  }
};

/**
 * Returns the maximum allowed duration (minutes) for a job given its position within
 * the employee's ordered list of segments.
 *
 * When timeWorked is INCLUDE, grace period time spills across adjacent segment boundaries:
 *   - First segment: only end-side grace applies (nothing before it)
 *   - Last segment:  only start-side grace applies (nothing after it)
 *   - Middle:        both sides apply
 *   - Single (first AND last): neither side applies — same as EXCLUDE
 */
const getMaxSegmentDurationMinutes = (segmentMinutes: number, isFirst: boolean, isLast: boolean, gracePeriodPolicy: IDaySegmentGracePeriodPolicy | undefined): number => {
  if (gracePeriodPolicy?.timeWorked !== GracePeriodTimeWorked.INCLUDE) {
    return segmentMinutes;
  }
  const startGrace = isFirst ? 0 : (gracePeriodPolicy.segmentStart ?? 0);
  const endGrace = isLast ? 0 : (gracePeriodPolicy.segmentEnd ?? 0);
  return segmentMinutes + startGrace + endGrace;
};

const LaborData = ({ currentCalculatorRules, site, onBack, pageHeaderContent }: ILaborDataProps) => {
  const dispatch = useDispatch();
  const paymentSet = useSelector(selectCalculatorPaymentSet());
  const paymentSetSegments = paymentSet?.segments;
  const paymentDate = paymentSet?.date;

  /**
   * this clone is used throughout the component to store any
   * changes made and will be compared to the original to determine
   * if the user has made any changes
   */
  const posResults = useSelector(selectCalculatorPOSResults());
  const [posDataClone, setPOSDataClone] = useState(() => structuredClone(posResults));
  const dirty = useMemo(() => !isEqual(posResults, posDataClone), [posResults, posDataClone]);

  // Change log entries
  const changeLogEntries = useSelector(selectCalculatorChangeLog());
  const [currentChangeLogEntries, setCurrentChangeLogEntries] = useState<ITipCalculatorChangeLogEntry[]>(changeLogEntries);

  // Payee pending removal
  const [payeeToRemove, setPayeeToRemove] = useState<ILaborDataPayee>();

  const hasActiveErrors = useMemo(() => posDataClone.data.some((data) => data.totals.totalMinWorked < 0 || data.segments.some((segment) => segment.totals.totalMinWorked < 0)), [posDataClone]);

  const laborDataTotals = useMemo(() => calculateTipCalculatorLaborDataTotals(posDataClone, paymentSetSegments, currentCalculatorRules), [posDataClone, paymentSetSegments, currentCalculatorRules]);

  const validateJobData = (jobData: IEmployeeLaborJobState[]) => {
    const gracePeriodPolicy = site?.settings?.gracePeriodPolicy;

    // jobData is pre-sorted by daySegment start time (see payeesList); array index == segment position.
    return jobData.map((job, positionIndex) => {
      // persist existing duration formatting error
      if (job.duration < 0 && job.error?.field === 'duration') {
        return job;
      }

      // validate that the duration of a job does not exceed the allowed maximum.
      // when the site's grace period policy includes time worked, employees may
      // legitimately work beyond the raw segment length — allow up to
      // (segment + gracePeriodStart + gracePeriodEnd) minutes based on segment position.
      if (job.duration && job.daySegment) {
        const segmentMinutes = getTotalSegmentMinutes(job.daySegment.start, job.daySegment.end);

        if (segmentMinutes < 0) {
          return {
            ...job,
            error: {
              type: EmployeeLaborJobErrorType.InvalidData,
              message: 'Invalid duration format. Expected hh:mm.',
              field: 'duration',
            } as IEmployeeLaborJobError,
          };
        }

        const isFirst = positionIndex === 0;
        const isLast = positionIndex === jobData.length - 1;

        const maxDurationMinutes = getMaxSegmentDurationMinutes(segmentMinutes, isFirst, isLast, gracePeriodPolicy);

        // Only enforce the grace-extended cap for manually edited values.
        // POS-imported durations (touched === false) may legitimately exceed the cap.
        if (job.touched && job.duration > maxDurationMinutes) {
          return {
            ...job,
            error: {
              type: EmployeeLaborJobErrorType.InvalidData,
              message: 'Duration can not be longer than the selected day segment.',
              field: 'duration',
            } as IEmployeeLaborJobError,
          };
        }
      }

      // validate that the total duration for an employee does not exceed 24 hours
      const totalDuration = jobData.reduce((acc, data) => acc + data.duration, 0);
      if (totalDuration > Duration.MAX_DAILY_MINUTES) {
        return {
          ...job,
          error: {
            type: EmployeeLaborJobErrorType.InvalidData,
            message: 'Total duration for an employee can not be over 24 hours.',
            field: 'duration',
          } as IEmployeeLaborJobError,
        };
      }

      return { ...job, error: undefined };
    });
  };

  const ruleEligibleJobTitles = useMemo(() => getRuleEligibleJobTitles(currentCalculatorRules.rules), [currentCalculatorRules.rules]);
  const payeesList = useMemo<ILaborDataPayee[]>(() => buildLaborDataPayees(posDataClone, ruleEligibleJobTitles, paymentSetSegments), [posDataClone, ruleEligibleJobTitles, paymentSetSegments]);

  const handleJobChange = (payee: ILaborDataPayee, jobData: IEmployeeLaborJobState[], deletedJob?: IEmployeeLaborJobState) => {
    // update duration
    jobData.forEach((data) => {
      const duration = data.error ? -1 : data.duration;
      const segment = posDataClone.data[payee.dataIndex].segments.find((s) => s.id === data.daySegment.id && s.job.title.toLowerCase() === data.jobTitle.title.toLowerCase());

      if (segment && segment.totals.totalMinWorked !== duration) {
        setCurrentChangeLogEntries(
          buildLaborChangeLogEntries(ChangeLogAction.SEGMENT_DURATION_EDITED, currentChangeLogEntries, {
            id: payee.id,
            name: payee.name,
            segmentName: segment.name,
            jobTitle: data.jobTitle.title.toLowerCase(),
            from: segment.totals.totalMinWorked,
            to: duration,
          })
        );

        // Update the segment duration
        segment.totals.totalMinWorked = duration;
      }
    });

    // Remove deleted job
    if (deletedJob) {
      const segmentIndex = posDataClone.data[payee.dataIndex].segments.findIndex((s) => s.id === deletedJob.daySegment.id && s.job.title.toLowerCase() === deletedJob.jobTitle.title.toLowerCase());
      if (segmentIndex >= 0) {
        setCurrentChangeLogEntries(
          buildLaborChangeLogEntries(ChangeLogAction.SEGMENT_REMOVED, currentChangeLogEntries, { id: payee.id, name: payee.name, segmentName: deletedJob.daySegment.name, jobTitle: deletedJob.jobTitle.title.toLowerCase() })
        );
        posDataClone.data[payee.dataIndex].segments.splice(segmentIndex, 1);
      }
    }

    setPOSDataClone({ ...posDataClone });
  };

  const [showIntegrationDataProcessingModal, hideIntegrationDataProcessingModal] = useModal(
    () => (
      <TipCalculatorProcessingModal
        mode={TipCalculatorProcessingModalMode.RECALCULATE}
        tipCalcRules={currentCalculatorRules}
        onClose={hideIntegrationDataProcessingModal}
        businessDate={paymentDate}
        site={site}
        segments={paymentSetSegments}
        posDataResults={posDataClone}
        changeLogEntries={currentChangeLogEntries}
        onSuccess={() => dispatch(setPOSResults(posDataClone))}
      />
    ),
    [currentCalculatorRules, paymentDate, site, paymentSet, posDataClone]
  );

  const [showRemoveEmployeeModal, hideRemoveEmployeeModal] = useModal(() => {
    if (!payeeToRemove) return null;

    const handleConfirmRemove = () => {
      setCurrentChangeLogEntries(buildLaborChangeLogEntries(ChangeLogAction.REMOVED, currentChangeLogEntries, { id: payeeToRemove.id, name: payeeToRemove.name }));
      setPOSDataClone({ ...posDataClone, data: posDataClone.data.filter((_, i) => i !== payeeToRemove.dataIndex) });
      hideRemoveEmployeeModal();
    };

    return (
      <ModalFactory customModal onClose={hideRemoveEmployeeModal} isActive>
        {!payeeToRemove.hasSplitChecks ? (
          <AlertContent
            title={`Remove ${payeeToRemove.name}?`}
            message={REMOVE_EMPLOYEE_MESSAGE}
            leftActionLabel="Cancel"
            rightActionLabel="Remove"
            rightActionAppearance="destructive"
            onLeftAction={hideRemoveEmployeeModal}
            onRightAction={handleConfirmRemove}
            maxWidth={420}
          />
        ) : (
          <AlertContent title="Unable to Remove Employee" message={SPLIT_CHECK_REMOVE_MESSAGE} rightActionLabel="Return to Payment" onRightAction={hideRemoveEmployeeModal} hideLeftAction maxWidth={420} />
        )}
      </ModalFactory>
    );
  }, [payeeToRemove, posDataClone, currentChangeLogEntries]);

  const onRemoveEmployee = (payee: ILaborDataPayee) => {
    setPayeeToRemove(payee);
    showRemoveEmployeeModal();
  };

  const [showAbandonChangesModal, hideAbandonChangesModal] = useModal(
    () => (
      <Alert
        dataCy="abandon-changes-alert"
        title="Abandon Changes?"
        message="You have unsaved changes that haven't been applied to your calculations. Are you sure you want to discard edits?"
        onLeftAction={hideAbandonChangesModal}
        onRightAction={onBack}
        leftActionLabel="Cancel"
        rightActionLabel="Abandon Changes"
        rightActionAppearance="destructive"
        isActive
      />
    ),
    [onBack]
  );

  return (
    <LayoutSidebar
      content={
        <>
          {pageHeaderContent}

          <MPContent data-cy="labor-data-list">
            <SectionHeader data-cy="labor-data-header" title={`Labor Data (${payeesList.length})`} />
            {!payeesList.length ? (
              <Empty container containerHeight={250} icon="People" message="No Payees" />
            ) : (
              payeesList.map((payee) => (
                <EmployeeListItem
                  key={`labor-payee-${payee.id}`}
                  laborData={payee.labor}
                  timecardData={payee.provider}
                  paymentDate={paymentDate}
                  timezone={site?.settings.timezone}
                  userId={payee.id}
                  username={payee.name}
                  onRemoveEmployee={() => onRemoveEmployee(payee)}
                  removeDisabled={payeesList.length === 1}
                  onJobChange={(jobData: IEmployeeLaborJobState[], deletedJob?: IEmployeeLaborJobState) => handleJobChange(payee, jobData, deletedJob)}
                  validate={validateJobData}
                  autoClockOut={payee.autoClockOut}
                  hasSplitChecks={payee.hasSplitChecks}
                />
              ))
            )}
          </MPContent>
        </>
      }
      sidebar={
        <TipCalcLaborDataSidebar
          businessDate={dayjs(paymentDate)}
          onBack={dirty ? showAbandonChangesModal : onBack}
          onContinue={() => showIntegrationDataProcessingModal()}
          totals={laborDataTotals}
          continueDisabled={hasActiveErrors || !posDataClone.data.length || !dirty || laborDataTotals.totalTippedMinutes <= 0}
        />
      }
    />
  );
};

export default LaborData;
